import React from 'react';
import clsx from 'clsx';
import {
  Description,
  ErrorMessage,
  Field,
  Label,
} from '../common/Catalyst/fieldset';
import { Input } from '../common/Catalyst/input';
import { Switch } from '../common/Catalyst/switch';
import { FieldPicker } from '../common/Custom/FieldPicker';
import { EnumDescriptor } from './enumLabels';

// The four control shapes the billing policy form needs, so the form
// itself reads as a list of settings rather than a wall of markup.

interface BaseProps {
  label: string;
  help?: React.ReactNode;
  error?: string;
  disabled?: boolean;
}

interface TextProps extends BaseProps {
  value: string;
  onChange: (next: string) => void;
  /** Rendered inside the input's left edge — `$` or nothing. */
  prefix?: string;
  /** Rendered after the input — `%`, `days`. */
  suffix?: string;
  placeholder?: string;
}

/**
 * A number the user types, held as a string all the way through.
 *
 * Money never becomes a float, and a blank has to be representable —
 * on a nullable field it is what clears the value.
 */
export const TextSetting: React.FC<TextProps> = ({
  label,
  help,
  error,
  value,
  onChange,
  prefix,
  suffix,
  placeholder,
  disabled,
}) => (
  <Field disabled={disabled}>
    <Label>{label}</Label>
    {help && <Description>{help}</Description>}
    <div className="flex items-center gap-2">
      {prefix && (
        <span className="text-sm text-zinc-500 dark:text-zinc-400">
          {prefix}
        </span>
      )}
      <Input
        value={value}
        invalid={!!error}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="max-w-[12rem]"
      />
      {suffix && (
        <span className="text-sm text-zinc-500 dark:text-zinc-400">
          {suffix}
        </span>
      )}
    </div>
    {error && <ErrorMessage>{error}</ErrorMessage>}
  </Field>
);

interface EnumProps extends BaseProps {
  value: string;
  onChange: (next: string) => void;
  options: EnumDescriptor;
}

/**
 * A select built from a generated enum const.
 *
 * Never a hardcoded option list: a value the backend adds shows up here
 * as a title-cased option rather than going missing, and the help text
 * under the control follows whichever option is chosen.
 */
export const EnumSetting: React.FC<EnumProps> = ({
  label,
  help,
  error,
  value,
  onChange,
  options,
  disabled,
}) => {
  const chosen = options.help(value);
  return (
    <Field disabled={disabled}>
      <Label>{label}</Label>
      {help && <Description>{help}</Description>}
      <FieldPicker
        label={label}
        value={value}
        disabled={disabled}
        onChange={onChange}
        className="w-full max-w-md"
        options={options.options().map((o) => ({
          value: o.value,
          label: o.label,
          disabled: o.disabled,
        }))}
      />
      {chosen && <Description className="mt-1">{chosen}</Description>}
      {error && <ErrorMessage>{error}</ErrorMessage>}
    </Field>
  );
};

interface BoolProps extends BaseProps {
  value: boolean;
  onChange: (next: boolean) => void;
}

export const BoolSetting: React.FC<BoolProps> = ({
  label,
  help,
  value,
  onChange,
  disabled,
}) => (
  <Field
    disabled={disabled}
    className={clsx('flex items-start justify-between gap-4')}
  >
    <div className="min-w-0">
      <Label>{label}</Label>
      {help && <Description>{help}</Description>}
    </div>
    <Switch
      checked={value}
      disabled={disabled}
      onChange={onChange}
      aria-label={label}
    />
  </Field>
);
